import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Directive, Input, EventEmitter, Output, QueryList, ContentChildren, ContentChild, Component, ChangeDetectionStrategy, ViewChild, NgModule } from '@angular/core';
import * as i4 from '@purespectrum1/ui/button';
import { ButtonModule } from '@purespectrum1/ui/button';
import * as i1 from '@purespectrum1/ui/select-dropdown';
import { SelectDropdownModule } from '@purespectrum1/ui/select-dropdown';
import * as i2 from 'ngx-infinite-scroll';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { BehaviorSubject, merge, combineLatest, Subscriber, of, fromEvent, timer, Subject, iif } from 'rxjs';
import { scan, filter, tap, debounceTime, map, shareReplay, switchMap, startWith, distinctUntilChanged } from 'rxjs/operators';
import * as i3$1 from '@angular/forms';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

class TableCaptionDirective {
    constructor(template) {
        this.template = template;
        this.placement = 'top';
    }
}
TableCaptionDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableCaptionDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive });
TableCaptionDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.12", type: TableCaptionDirective, selector: "[psUiTableCaption]", inputs: { placement: "placement" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableCaptionDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[psUiTableCaption]',
                }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }]; }, propDecorators: { placement: [{
                type: Input
            }] } });

class TableCellTemplateDirective {
    constructor(template) {
        this.template = template;
        this.tap = new EventEmitter();
    }
}
TableCellTemplateDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableCellTemplateDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive });
TableCellTemplateDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.12", type: TableCellTemplateDirective, selector: "[psUiTableCellTemplate]", outputs: { tap: "tap" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableCellTemplateDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[psUiTableCellTemplate]',
                }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }]; }, propDecorators: { tap: [{
                type: Output
            }] } });

class TableSelectionState {
    constructor(selection, action, selectedBy) {
        this.selection = selection;
        this.action = action;
        this.selectedBy = selectedBy;
    }
    static none() {
        return new TableSelectionState([], 'none');
    }
    static from(selection) {
        return new TableSelectionState(selection.data, selection.action);
    }
    update(selection) {
        if (['all', 'none', 'input'].includes(selection.action)) {
            return TableSelectionState.from(selection);
        }
        return this.select(selection);
    }
    select(selection) {
        const key = selection.data.pop();
        if (this.selection.includes(key)) {
            const filtered = this.selection.filter((element) => element !== key);
            return new TableSelectionState(filtered, selection.action);
        }
        return new TableSelectionState([...this.selection, key], selection.action);
    }
    selected(keys) {
        return this.selection.includes(keys);
    }
    current() {
        return this.selection;
    }
    empty() {
        return this.selection.length === 0;
    }
    copyWith({ selection, action, selectedBy, }) {
        return new TableSelectionState(selection !== null && selection !== void 0 ? selection : this.selection, action !== null && action !== void 0 ? action : this.action, selectedBy !== null && selectedBy !== void 0 ? selectedBy : this.selectedBy);
    }
}

class TableCheckboxColumnDirective {
    constructor() {
        this.showSelectAll = true;
        this.disable = false;
        this.selectedKeysChange = new EventEmitter();
        this.showCommand = new BehaviorSubject(true);
        this.selectCommand$ = new BehaviorSubject({ action: 'input', data: [] });
        this.selectByCommand$ = new BehaviorSubject(undefined);
        this.selectedBy$ = this.selectByCommand$.pipe();
        const state$ = this.selectCommand$.pipe(scan((state, selection) => state.update(selection), TableSelectionState.none()));
        const action$ = state$.pipe(filter((state) => state.action !== 'input'), tap((state) => this.selectedKeysChange.emit(state.current())));
        const input$ = state$.pipe(filter((state) => state.action === 'input'));
        const selection$ = merge(action$, input$);
        this.selected$ = combineLatest([this.selectedBy$, selection$]).pipe(debounceTime(100), map(([selectedBy, state]) => state.copyWith({ selectedBy })));
    }
    set show(status) {
        this.showCommand.next(status);
    }
    set selectBy(key) {
        this.selectByCommand$.next(key);
    }
    set selectedKeys(keys) {
        this.selectCommand$.next({ action: 'input', data: keys });
    }
    get show$() {
        return this.showCommand.asObservable();
    }
    select(selected) {
        return Array.isArray(selected)
            ? this._selectMany(selected)
            : this._select(selected);
    }
    _select(selected) {
        const action = {
            data: [selected],
            action: 'selection',
        };
        this.selectCommand$.next(action);
    }
    _selectMany(selected) {
        const action = {
            data: selected,
            action: selected.length > 1 ? 'all' : 'none',
        };
        this.selectCommand$.next(action);
    }
}
TableCheckboxColumnDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableCheckboxColumnDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
TableCheckboxColumnDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.12", type: TableCheckboxColumnDirective, selector: "[psUiTableCheckboxColumn]", inputs: { show: "show", showSelectAll: "showSelectAll", selectBy: "selectBy", selectedKeys: "selectedKeys", disable: "disable" }, outputs: { selectedKeysChange: "selectedKeysChange" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableCheckboxColumnDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[psUiTableCheckboxColumn]',
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { show: [{
                type: Input
            }], showSelectAll: [{
                type: Input
            }], selectBy: [{
                type: Input
            }], selectedKeys: [{
                type: Input
            }], disable: [{
                type: Input
            }], selectedKeysChange: [{
                type: Output
            }] } });

class TableColumnCustomHeaderDirective {
    constructor(template) {
        this.template = template;
    }
}
TableColumnCustomHeaderDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableColumnCustomHeaderDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive });
TableColumnCustomHeaderDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.12", type: TableColumnCustomHeaderDirective, selector: "[psUiTableColumnCustomHeader]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableColumnCustomHeaderDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[psUiTableColumnCustomHeader]',
                }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }]; } });

class TableColumnHeaderOptionDirective {
    constructor(element) {
        this.element = element;
    }
    option() {
        const text = this.element.nativeElement.innerHTML.trim();
        const value = this.element.nativeElement.value.trim();
        return { text, value };
    }
}
TableColumnHeaderOptionDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableColumnHeaderOptionDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
TableColumnHeaderOptionDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.12", type: TableColumnHeaderOptionDirective, selector: "option=[psUiTableColumnOption]", inputs: { group: "group" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableColumnHeaderOptionDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'option=[psUiTableColumnOption]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { group: [{
                type: Input
            }] } });

class TableColumnFilterDirective {
    constructor() {
        this.options = new QueryList();
        this.showCommand = new BehaviorSubject(true);
    }
    set show(status) {
        this.showCommand.next(status);
    }
    get show$() {
        return this.showCommand.asObservable();
    }
}
TableColumnFilterDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableColumnFilterDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
TableColumnFilterDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.12", type: TableColumnFilterDirective, selector: "[psUiTableColumnFilter]", inputs: { delegate: "delegate", show: "show" }, queries: [{ propertyName: "options", predicate: TableColumnHeaderOptionDirective }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableColumnFilterDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[psUiTableColumnFilter]',
                }]
        }], propDecorators: { delegate: [{
                type: Input
            }], show: [{
                type: Input
            }], options: [{
                type: ContentChildren,
                args: [TableColumnHeaderOptionDirective]
            }] } });

class TableColumnFooterDirective {
    constructor(template) {
        this.template = template;
    }
}
TableColumnFooterDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableColumnFooterDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive });
TableColumnFooterDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.12", type: TableColumnFooterDirective, selector: "[psUiColumnFooter]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableColumnFooterDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[psUiColumnFooter]',
                }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }]; } });

class TableColumnDirective {
    constructor() {
        this.field = '';
        this.searchable = false;
        this.multi = true;
        this.searchDropdownItems = false;
        this.showClearStateButton = false;
        this.sortable = false;
        this.searchType = 'start';
        this.truncateWord = true;
        this.textAlignment = 'start';
        this.showCommand = new BehaviorSubject(true);
        this.filterCommand = new BehaviorSubject([]);
        this._subscriber = new Subscriber();
        this.filters$ = this.filterCommand.pipe(map((options) => options.map((element) => element.option())));
        this.groups$ = this.filterCommand.pipe(map((options) => options.reduce((acc, next, index) => (Object.assign(Object.assign({}, acc), { [index]: next.group })), {})));
    }
    set show(status) {
        this.showCommand.next(status);
    }
    get template() {
        var _a;
        return (_a = this.content) === null || _a === void 0 ? void 0 : _a.template;
    }
    get delegate() {
        var _a;
        return (_a = this.filter) === null || _a === void 0 ? void 0 : _a.delegate;
    }
    get show$() {
        return this.showCommand.asObservable();
    }
    get filterable$() {
        var _a, _b;
        return (_b = (_a = this.filter) === null || _a === void 0 ? void 0 : _a.show$) !== null && _b !== void 0 ? _b : of(false);
    }
    ngAfterViewInit() {
        var _a, _b;
        this._subscriber.add((_a = this.filter) === null || _a === void 0 ? void 0 : _a.options.changes.subscribe((query) => {
            return this.filterCommand.next(query);
        }));
        (_b = this.filter) === null || _b === void 0 ? void 0 : _b.options.notifyOnChanges();
    }
    ngOnDestroy() {
        this._subscriber.unsubscribe();
    }
    space() {
        if (!this.width) {
            return null;
        }
        if (this.width === 'fit') {
            return this.filter ? '15%' : '1%';
        }
        if (this.width.toString().includes('%')) {
            return this.width.toString();
        }
        return `${this.width}px`;
    }
}
TableColumnDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableColumnDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
TableColumnDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.12", type: TableColumnDirective, selector: "[psUiTableColumn]", inputs: { title: "title", show: "show", field: "field", searchable: "searchable", multi: "multi", searchDropdownItems: "searchDropdownItems", showClearStateButton: "showClearStateButton", sortable: "sortable", searchType: "searchType", width: "width", truncateWord: "truncateWord", textAlignment: "textAlignment" }, queries: [{ propertyName: "content", first: true, predicate: TableCellTemplateDirective, descendants: true }, { propertyName: "header", first: true, predicate: TableColumnCustomHeaderDirective, descendants: true }, { propertyName: "filter", first: true, predicate: TableColumnFilterDirective, descendants: true }, { propertyName: "footer", first: true, predicate: TableColumnFooterDirective, descendants: true }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableColumnDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[psUiTableColumn]',
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { title: [{
                type: Input
            }], show: [{
                type: Input
            }], field: [{
                type: Input
            }], searchable: [{
                type: Input
            }], multi: [{
                type: Input
            }], searchDropdownItems: [{
                type: Input
            }], showClearStateButton: [{
                type: Input
            }], sortable: [{
                type: Input
            }], searchType: [{
                type: Input
            }], width: [{
                type: Input
            }], truncateWord: [{
                type: Input
            }], textAlignment: [{
                type: Input
            }], content: [{
                type: ContentChild,
                args: [TableCellTemplateDirective]
            }], header: [{
                type: ContentChild,
                args: [TableColumnCustomHeaderDirective]
            }], filter: [{
                type: ContentChild,
                args: [TableColumnFilterDirective]
            }], footer: [{
                type: ContentChild,
                args: [TableColumnFooterDirective]
            }] } });

class TableExpandableContentDirective {
    constructor(template) {
        this.template = template;
    }
}
TableExpandableContentDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableExpandableContentDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive });
TableExpandableContentDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.12", type: TableExpandableContentDirective, selector: "[psUiTableExpandableContent]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableExpandableContentDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[psUiTableExpandableContent]',
                }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }]; } });

class ExpandOne {
    constructor(_row, _manualClose) {
        this._row = _row;
        this._manualClose = _manualClose;
    }
    expanded(row) {
        if (this._row === 'all') {
            return true;
        }
        return row === this._row;
    }
    expand(row) {
        if (this.expanded(row) && !this._manualClose) {
            return this.retract(row);
        }
        return new ExpandOne(row, this._manualClose);
    }
    retract(row) {
        return new ExpandOne('none', this._manualClose);
    }
    row() {
        return this._row;
    }
}
class ExpandEach {
    constructor(row, manualClose) {
        this._all = row === 'all';
        this._none = row === 'none';
        this._rows = Array.isArray(row) ? row : [row];
        this._manualClose = manualClose;
    }
    expanded(row) {
        if (this._all || this._none) {
            return this._all || !this._none;
        }
        if (typeof row === 'number') {
            return this._rows.includes(row);
        }
        return false;
    }
    expand(row) {
        if (this.expanded(row) && !this._manualClose) {
            return this.retract(row);
        }
        const rows = Array.isArray(row) ? row : [row];
        return new ExpandEach([...new Set([...this._rows, ...rows])], this._manualClose);
    }
    retract(row) {
        const rows = this._rows.filter((r) => r !== row);
        return new ExpandEach(rows, this._manualClose);
    }
    row() {
        if (this._all) {
            return 'all';
        }
        if (this._none) {
            return 'none';
        }
        return this._rows;
    }
}
class ExpandableState {
    constructor(_behavior, _manualClose = false) {
        this._behavior = _behavior;
        this._manualClose = _manualClose;
    }
    static init(type = 'one', manualClose = false) {
        const behavior = ExpandableState._instatiate(type, manualClose);
        return new ExpandableState(behavior, manualClose);
    }
    static _instatiate(type = 'one', manualClose = false) {
        switch (type) {
            case 'one':
                return new ExpandOne('none', manualClose);
            case 'each':
                return new ExpandEach('none', manualClose);
        }
    }
    expanded(row) {
        return this._behavior.expanded(row);
    }
    expand(row) {
        return this._behavior.expand(row);
    }
    retract(row) {
        return this._behavior.retract(row);
    }
    row() {
        return this._behavior.row();
    }
}

class TableExpandableRowDirective {
    constructor() {
        /*
         * Define the expandable behavior
         * 'one' - Expand one at time
         * 'each' - Expand each independently
         * */
        this.behavior = 'one';
        this.manualClose = false;
        this.expandedCommand$ = new BehaviorSubject('none');
        this.retractedCommand$ = new BehaviorSubject('none');
    }
    get template() {
        var _a;
        return (_a = this.content) === null || _a === void 0 ? void 0 : _a.template;
    }
    ngOnInit() {
        const expand$ = this.expandedCommand$.pipe(map((row) => ({ row, type: 'expand' })));
        const retract$ = this.retractedCommand$.pipe(filter(() => this.manualClose), map((row) => ({ row, type: 'retract' })));
        this.expanded$ = merge(expand$, retract$).pipe(scan((state, { row, type }) => type === 'expand' ? state.expand(row) : state.retract(row), ExpandableState.init(this.behavior, this.manualClose)), shareReplay(1));
    }
    expand(row) {
        this.expandedCommand$.next(row);
    }
    retract(row) {
        this.retractedCommand$.next(row);
    }
}
TableExpandableRowDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableExpandableRowDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
TableExpandableRowDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.12", type: TableExpandableRowDirective, selector: "[psUiTableExpandableRow]", inputs: { behavior: "behavior", manualClose: "manualClose" }, queries: [{ propertyName: "content", first: true, predicate: TableExpandableContentDirective, descendants: true }, { propertyName: "columns", predicate: TableColumnDirective }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableExpandableRowDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[psUiTableExpandableRow]',
                }]
        }], propDecorators: { columns: [{
                type: ContentChildren,
                args: [TableColumnDirective]
            }], content: [{
                type: ContentChild,
                args: [TableExpandableContentDirective]
            }], behavior: [{
                type: Input
            }], manualClose: [{
                type: Input
            }] } });

class TableFilter {
    constructor(filters) {
        this.filters = filters;
    }
    static from(state) {
        return new TableFilter(state.filter);
    }
    allow(element) {
        const allow = this.filters.reduce((acc, filter) => {
            if (!acc) {
                return acc;
            }
            if (!filter.apply()) {
                return acc;
            }
            return filter.allow(element);
        }, true);
        return allow;
    }
}
class FilterValue {
    constructor(element) {
        this.element = element;
    }
    cast(key) {
        const value = this.element[key];
        if (typeof value === 'string') {
            return value;
        }
        if (typeof value === 'number') {
            return value.toString();
        }
        return `${value}`;
    }
}
class EmptyFilter {
    allow(_) {
        return true;
    }
    match(_) {
        return false;
    }
    apply() {
        return true;
    }
    values() {
        return {
            field: 'empty',
            type: 'none',
            values: [],
        };
    }
}
class StrictFilter {
    constructor(_key, _values) {
        this._key = _key;
        this._values = _values;
    }
    allow(element) {
        return this._values.some((value) => value === new FilterValue(element).cast(this._key));
    }
    match(field) {
        return this._key === field;
    }
    apply() {
        return this._values.length > 0;
    }
    values() {
        return {
            field: this._key,
            type: 'option',
            values: this._values,
        };
    }
}
class PartialFilter {
    constructor(_key, _value, type = 'start') {
        this._key = _key;
        this._value = _value;
        this.type = type;
        this._regex = new RegExp(`^${_value}`, 'i');
    }
    allow(element) {
        const value = new FilterValue(element).cast(this._key);
        if (this.type === 'contains') {
            return value.toLowerCase().includes(this._value.toLowerCase());
        }
        return this._regex.test(value);
    }
    match(field) {
        return this._key === field;
    }
    apply() {
        return this._value.length > 0;
    }
    values() {
        return {
            field: this._key,
            type: 'search',
            values: [this._value],
        };
    }
}
class CustomFilter {
    constructor(_field, _values, _delegate) {
        this._field = _field;
        this._values = _values;
        this._delegate = _delegate;
    }
    apply() {
        return this._values.length > 0;
    }
    allow(element) {
        const filter = this._delegate.filter(this._field, this._values);
        return filter.allow(element);
    }
    match(field) {
        return this._field === field;
    }
    values() {
        return {
            field: this._field,
            type: 'custom',
            values: this._values,
        };
    }
}
class TableFilterFactory {
    constructor(_filters, _columns) {
        this._filters = _filters;
        this._columns = _columns;
    }
    create() {
        return this._filters.map((filter) => {
            const column = this._columns.find((c) => c.field === filter.field);
            if (!column) {
                return new StrictFilter(filter.field, filter.values);
            }
            return this._filter(filter, column);
        });
    }
    _filter(filter, column) {
        switch (filter.type) {
            case 'option':
                return new StrictFilter(filter.field, filter.values);
            case 'search':
                return new PartialFilter(filter.field, filter.values[0], column.searchType);
            case 'custom':
                return new CustomFilter(filter.field, filter.values, column.delegate);
            default:
                return new EmptyFilter();
        }
    }
}

class TableColumnHeaderComponent {
    constructor() {
        this.items = [];
        this.searchable = false;
        this.multi = true;
        this.searchDropdownItems = false;
        this.showClearStateButton = false;
        this.sortable = false;
        this.filterable = false;
        this.sorted = 'none';
        this.groups = {};
        this.truncateWord = true;
        this.textAlignment = 'start';
        this.searchType = 'start';
        this.sort = new EventEmitter();
        this.filter = new EventEmitter();
        this.clearFilterButton = false;
        this.filters = new FormControl([]);
        this.searchText = '';
        this._filtered = [];
    }
    set filtered(filters) {
        this._filtered = filters;
        this._selectFilters(filters);
    }
    clearState() {
        const base = {
            column: this.title,
            field: this.field,
            sort: this.sorted,
            filter: [],
        };
        this._filtered = [];
        this.filter.emit(base);
        this.clearFilterButton = false;
    }
    onFilter(options) {
        const base = {
            column: this.title,
            field: this.field,
            sort: this.sorted,
        };
        const values = options.map((option) => option === null || option === void 0 ? void 0 : option.value);
        const filter = [
            ...this._filtered.filter((f) => !(f instanceof StrictFilter)),
            new StrictFilter(this.field, values),
        ];
        this.filter.emit(Object.assign(Object.assign({}, base), { filter: this.delegate ? this._custom(values) : filter }));
        this.clearFilterButton = this.showClearStateButton || false;
    }
    onSort(type) {
        this.sort.emit({
            column: this.title,
            field: this.field,
            sort: this.sorted === type ? 'none' : type,
            filter: this._filtered,
        });
    }
    onSearch(openText) {
        const filterText = this.searchDropdownItems ? '' : openText;
        const data = {
            column: this.title,
            field: this.field,
            sort: this.sorted,
            searchText: openText,
            searchField: this.field,
            filter: [
                ...this._filtered.filter((f) => !(f instanceof PartialFilter)),
                new PartialFilter(this.field, filterText || '', this.searchType),
            ],
        };
        this.sort.emit(data);
    }
    _custom(values) {
        return [
            ...this._filtered.filter((f) => !(f instanceof CustomFilter)),
            new CustomFilter(this.field, values, this.delegate),
        ];
    }
    _selectFilters(filters) {
        var _a, _b;
        const filterValues = filters.map((filter) => filter.values());
        const values = filterValues
            .filter((filter) => ['option', 'custom'].includes(filter.type))
            .reduce((acc, next) => [...acc, ...next.values], []);
        this.searchText =
            (_b = (_a = filterValues.find((filter) => filter.type === 'search')) === null || _a === void 0 ? void 0 : _a.values[0]) !== null && _b !== void 0 ? _b : '';
        this.filters = new FormControl(this.items.filter((item) => values.includes(item.value)));
        this.clearFilterButton = this.showClearStateButton || false;
    }
}
TableColumnHeaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableColumnHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
TableColumnHeaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.12", type: TableColumnHeaderComponent, selector: "ps-ui-table-column-header", inputs: { title: "title", items: "items", field: "field", searchable: "searchable", multi: "multi", searchDropdownItems: "searchDropdownItems", showClearStateButton: "showClearStateButton", sortable: "sortable", filterable: "filterable", sorted: "sorted", groups: "groups", filtered: "filtered", truncateWord: "truncateWord", textAlignment: "textAlignment", delegate: "delegate", searchType: "searchType" }, outputs: { sort: "sort", filter: "filter" }, ngImport: i0, template: "<p *ngIf=\"!filterable && !sortable && !searchable\" class=\"header-title\">\n  {{ title }}\n</p>\n<ps-ui-select-dropdown\n  #dropdown\n  *ngIf=\"sortable || searchable || filterable\"\n  field=\"text\"\n  [idField]=\"'text'\"\n  [multi]=\"multi\"\n  [groups]=\"groups\"\n  [dropdownItems]=\"filterable ? items : []\"\n  [label]=\"title\"\n  [labelAlignment]=\"textAlignment\"\n  [truncateWord]=\"truncateWord\"\n  [enableSearch]=\"searchable\"\n  [indicator]=\"true\"\n  [maxHeight]=\"200\"\n  [searchDropdownItems]=\"searchDropdownItems\"\n  [showClearStateButton]=\"clearFilterButton\"\n  [searchTerm]=\"searchText\"\n  (clearState)=\"clearState()\"\n  (search)=\"onSearch($event)\"\n  [loading]=\"false\"\n  [formControl]=\"filters\"\n  (selectedItems)=\"onFilter($event)\"\n  (selectedItem)=\"onFilter([$event])\"\n>\n  <ng-template psUiSelectDropdownIcon>\n    <i *ngIf=\"sorted === 'none'\" class=\"fa fa-sort\" aria-hidden=\"true\"></i>\n    <i\n      *ngIf=\"sorted === 'ascending'\"\n      class=\"fa fa-sort-asc\"\n      aria-hidden=\"true\"\n    ></i>\n    <i\n      *ngIf=\"sorted === 'descending'\"\n      class=\"fa fa-sort-desc\"\n      aria-hidden=\"true\"\n    ></i>\n  </ng-template>\n\n  <ng-template psUiSelectDropdownAction [enabled]=\"sortable\">\n    <button\n      psUiButton\n      [outline]=\"true\"\n      [size]=\"'small'\"\n      class=\"p-0\"\n      type=\"button\"\n      [ngClass]=\"{ 'btn-primary': sorted === 'ascending' }\"\n      (click)=\"onSort('ascending'); dropdown.toggle()\"\n    >\n      <i class=\"fa fa-arrow-up\" aria-hidden=\"true\"></i>\n    </button>\n    <button\n      psUiButton\n      [outline]=\"true\"\n      [size]=\"'small'\"\n      class=\"p-0\"\n      type=\"button\"\n      [ngClass]=\"{ 'btn-primary': sorted === 'descending' }\"\n      (click)=\"onSort('descending'); dropdown.toggle()\"\n    >\n      <i class=\"fa fa-arrow-down\" aria-hidden=\"true\"></i>\n    </button>\n  </ng-template>\n</ps-ui-select-dropdown>\n", styles: [":host{--custom-dropdown-position: unset;--custom-dropdown-left-offset: 0 !important;--custom-dropdown-top-offset: 2px !important;--custom-dropdown-action-button-width: 100%;--custom-dropdown-button-icon-margin: 2.5rem;.dropdown-button-label {text-overflow: nowrap; text-wrap: var(--custom-table-header-text-wrap);} .dropdown-menu {font-size: inherit; height: auto !important; max-height: 200px !important; contain: unset; min-width: var(--custom-sorting-button-width, 12rem); padding: 0; border: 0; border-radius: 0;}}p{padding:2px 0 0;margin:0}.header-title{text-wrap:nowrap}\n"], components: [{ type: i1.SelectDropdownComponent, selector: "ps-ui-select-dropdown", inputs: ["dropdownItems", "searchText", "idField", "field", "searchByKeys", "label", "labelNone", "labelAlignment", "spanField", "enableSearch", "clearSearch", "multi", "considerSingleSelectionAsAll", "allSelected", "firstSelected", "loading", "disabled", "icon", "iconClass", "newTextInput", "showNewTextField", "newTextSelectionType", "newTextInputPlaceHolder", "newInputLabel", "noItemMessage", "sortBySelected", "orderById", "orderBy", "mcIconIdentifier", "addNewItem", "addNewItemDropDown", "indicator", "searchDropdownItems", "showClearStateButton", "boundaries", "maxHeight", "eventListeningContext", "groups", "showFlag", "flagCountryCodeField", "flagLanguageCodeField", "truncateWord", "searchTerm"], outputs: ["clearState", "selectedItem", "selectedItems", "selectedFooterItem", "submitButtonClicked", "search"], exportAs: ["psUiSelectDropdown"] }], directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i3$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i3$1.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { type: i1.SelectDropdownIconDirective, selector: "[psUiSelectDropdownIcon]" }, { type: i1.SelectDropdownActionDirective, selector: "[psUiSelectDropdownAction]", inputs: ["enabled"] }, { type: i4.ButtonDirective, selector: "button[psUiButton]", inputs: ["disabled"] }, { type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableColumnHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ps-ui-table-column-header', changeDetection: ChangeDetectionStrategy.OnPush, template: "<p *ngIf=\"!filterable && !sortable && !searchable\" class=\"header-title\">\n  {{ title }}\n</p>\n<ps-ui-select-dropdown\n  #dropdown\n  *ngIf=\"sortable || searchable || filterable\"\n  field=\"text\"\n  [idField]=\"'text'\"\n  [multi]=\"multi\"\n  [groups]=\"groups\"\n  [dropdownItems]=\"filterable ? items : []\"\n  [label]=\"title\"\n  [labelAlignment]=\"textAlignment\"\n  [truncateWord]=\"truncateWord\"\n  [enableSearch]=\"searchable\"\n  [indicator]=\"true\"\n  [maxHeight]=\"200\"\n  [searchDropdownItems]=\"searchDropdownItems\"\n  [showClearStateButton]=\"clearFilterButton\"\n  [searchTerm]=\"searchText\"\n  (clearState)=\"clearState()\"\n  (search)=\"onSearch($event)\"\n  [loading]=\"false\"\n  [formControl]=\"filters\"\n  (selectedItems)=\"onFilter($event)\"\n  (selectedItem)=\"onFilter([$event])\"\n>\n  <ng-template psUiSelectDropdownIcon>\n    <i *ngIf=\"sorted === 'none'\" class=\"fa fa-sort\" aria-hidden=\"true\"></i>\n    <i\n      *ngIf=\"sorted === 'ascending'\"\n      class=\"fa fa-sort-asc\"\n      aria-hidden=\"true\"\n    ></i>\n    <i\n      *ngIf=\"sorted === 'descending'\"\n      class=\"fa fa-sort-desc\"\n      aria-hidden=\"true\"\n    ></i>\n  </ng-template>\n\n  <ng-template psUiSelectDropdownAction [enabled]=\"sortable\">\n    <button\n      psUiButton\n      [outline]=\"true\"\n      [size]=\"'small'\"\n      class=\"p-0\"\n      type=\"button\"\n      [ngClass]=\"{ 'btn-primary': sorted === 'ascending' }\"\n      (click)=\"onSort('ascending'); dropdown.toggle()\"\n    >\n      <i class=\"fa fa-arrow-up\" aria-hidden=\"true\"></i>\n    </button>\n    <button\n      psUiButton\n      [outline]=\"true\"\n      [size]=\"'small'\"\n      class=\"p-0\"\n      type=\"button\"\n      [ngClass]=\"{ 'btn-primary': sorted === 'descending' }\"\n      (click)=\"onSort('descending'); dropdown.toggle()\"\n    >\n      <i class=\"fa fa-arrow-down\" aria-hidden=\"true\"></i>\n    </button>\n  </ng-template>\n</ps-ui-select-dropdown>\n", styles: [":host{--custom-dropdown-position: unset;--custom-dropdown-left-offset: 0 !important;--custom-dropdown-top-offset: 2px !important;--custom-dropdown-action-button-width: 100%;--custom-dropdown-button-icon-margin: 2.5rem;.dropdown-button-label {text-overflow: nowrap; text-wrap: var(--custom-table-header-text-wrap);} .dropdown-menu {font-size: inherit; height: auto !important; max-height: 200px !important; contain: unset; min-width: var(--custom-sorting-button-width, 12rem); padding: 0; border: 0; border-radius: 0;}}p{padding:2px 0 0;margin:0}.header-title{text-wrap:nowrap}\n"] }]
        }], propDecorators: { title: [{
                type: Input
            }], items: [{
                type: Input
            }], field: [{
                type: Input
            }], searchable: [{
                type: Input
            }], multi: [{
                type: Input
            }], searchDropdownItems: [{
                type: Input
            }], showClearStateButton: [{
                type: Input
            }], sortable: [{
                type: Input
            }], filterable: [{
                type: Input
            }], sorted: [{
                type: Input
            }], groups: [{
                type: Input
            }], filtered: [{
                type: Input
            }], truncateWord: [{
                type: Input
            }], textAlignment: [{
                type: Input
            }], delegate: [{
                type: Input
            }], searchType: [{
                type: Input
            }], sort: [{
                type: Output
            }], filter: [{
                type: Output
            }] } });

class HorizontalScroll {
    constructor(element, _offset = 20) {
        this._offset = _offset;
        this._container = element.nativeElement;
    }
    get scrollWidth() {
        return this._container.scrollWidth;
    }
    get clientWidth() {
        return this._container.clientWidth;
    }
    get scrollOffset() {
        return this._container.scrollLeft;
    }
    get maxScrollOffset() {
        return this.scrollWidth - this.clientWidth - 1;
    }
    overflowed() {
        return this.scrollWidth > this.clientWidth;
    }
    canScrollLeft() {
        return this.scrollOffset > 0;
    }
    canScrollRight() {
        return this.scrollOffset < this.maxScrollOffset;
    }
    scrollLeft(offset) {
        this._container.scrollLeft -= offset || this._offset;
    }
    scrollRight(offset) {
        this._container.scrollLeft += offset || this._offset;
    }
}

class AscendingOrder {
    before() {
        return -1;
    }
    after() {
        return 1;
    }
    equal() {
        return 0;
    }
}
class DescendingOrder {
    before() {
        return 1;
    }
    after() {
        return -1;
    }
    equal() {
        return 0;
    }
}
class StringSort {
    constructor(_order) {
        this._order = _order;
    }
    order(a, b) {
        const valueA = a.toUpperCase();
        const valueB = b.toUpperCase();
        if (valueA < valueB) {
            return this._order.before();
        }
        if (valueA > valueB) {
            return this._order.after();
        }
        return this._order.equal();
    }
}
class NumberSort {
    constructor(_order) {
        this._order = _order;
    }
    order(a, b) {
        if (a < b) {
            return this._order.before();
        }
        if (a > b) {
            return this._order.after();
        }
        return this._order.equal();
    }
}
class DateSort {
    constructor(_order) {
        this._order = _order;
    }
    order(a, b) {
        const time = b.getTime() - a.getTime();
        if (time > 0) {
            return this._order.before();
        }
        if (time < 0) {
            return this._order.after();
        }
        return this._order.equal();
    }
}
class TableSort {
    constructor(_order, enable = true) {
        this._order = _order;
        this.enable = enable;
        this._string = new StringSort(_order);
        this._number = new NumberSort(_order);
        this._date = new DateSort(_order);
    }
    static from(sort) {
        const order = sort === 'ascending' ? new AscendingOrder() : new DescendingOrder();
        return new TableSort(order, sort !== 'none');
    }
    order(a, b) {
        if (!this.enable) {
            return this._order.equal();
        }
        if (typeof a === 'string' && typeof b === 'string') {
            return this._string.order(a, b);
        }
        if (typeof a === 'number' && typeof b === 'number') {
            return this._number.order(a, b);
        }
        if (a instanceof Date && b instanceof Date) {
            return this._date.order(a, b);
        }
        // Don't know which type of sort to apply
        return this._order.equal();
    }
}

class TableDataTransform {
    constructor(data, selecteds, state, trackBy) {
        this.data = data;
        this.selecteds = selecteds;
        this.state = state;
        this.trackBy = trackBy;
    }
    elements() {
        const field = this.state.sort.field;
        const sort = TableSort.from(this.state.sort.type);
        const filter = TableFilter.from(this.state);
        return [...this.data]
            .filter((element) => filter.allow(element))
            .sort((a, b) => sort.order(a[field], b[field]))
            .map((element, index) => {
            const key = this.selecteds.selectedBy
                ? element[this.selecteds.selectedBy]
                : index;
            const trackBy = this.trackBy ? element[this.trackBy] : index;
            return {
                key,
                trackBy,
                data: element,
                selected: this.selecteds.selected(key),
            };
        });
    }
}

class TableState {
    constructor(sort, searchText, searchField, filter = []) {
        this.sort = sort;
        this.searchText = searchText;
        this.searchField = searchField;
        this.filter = filter;
    }
    static from(event, columns) {
        const filters = new TableFilterFactory(event.filter, columns).create();
        return new TableState(event.sort, '', '', filters);
    }
    static empty() {
        return new TableState({ field: '', type: 'none' }, '', '');
    }
    sorted(field) {
        return this.sort.field === field ? this.sort.type : 'none';
    }
    filtered(field) {
        return this.filter.filter((f) => f.match(field));
    }
    update(event) {
        const sort = { field: event.field, type: event.sort };
        if (event.field === '') {
            return new TableState(sort, this.searchText, this.searchField, this.filter);
        }
        this.filter = [
            ...this.filter.filter((f) => !f.match(event.field)),
            ...event.filter,
        ];
        const searchText = event.searchText || '';
        const searchField = event.searchField || '';
        return new TableState(sort, searchText, searchField, this.filter);
    }
    event() {
        const filter = this.filter.map((f) => f.values());
        return {
            sort: this.sort,
            filter,
            searchText: this.searchText,
            searchField: this.searchField,
        };
    }
}

class LongPressDirective {
    constructor(elementRef) {
        this.threshold = 500;
        this.longPress = new EventEmitter();
        const mousedown = fromEvent(elementRef.nativeElement, 'mousedown').pipe(filter((event) => event.button == 0), // Only allow left button (Primary button)
        map(() => true) // turn on threshold counter
        );
        const touchstart = fromEvent(elementRef.nativeElement, 'touchstart').pipe(map(() => true));
        const touchEnd = fromEvent(elementRef.nativeElement, 'touchend').pipe(map(() => false));
        const mouseup = fromEvent(window, 'mouseup').pipe(filter((event) => event.button == 0), // Only allow left button (Primary button)
        map(() => false) // reset threshold counter
        );
        this._subscription = merge(mousedown, mouseup, touchstart, touchEnd)
            .pipe(switchMap((state) => (state ? timer(this.threshold, 100) : of(null))), filter((value) => !!value))
            .subscribe(() => this.longPress.emit());
    }
    ngOnDestroy() {
        if (this._subscription) {
            this._subscription.unsubscribe();
        }
    }
}
LongPressDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LongPressDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
LongPressDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.12", type: LongPressDirective, selector: "[psUiLongPress]", inputs: { threshold: "threshold" }, outputs: { longPress: "longPress" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LongPressDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[psUiLongPress]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { threshold: [{
                type: Input
            }], longPress: [{
                type: Output
            }] } });

class TableComponent {
    constructor() {
        this.scroll = { distance: 2, throttle: 20 };
        this.border = 'bottom';
        this.headless = false;
        this.disableHorizontalScrollingButtons = false;
        this.scrolled = new EventEmitter();
        this.stateChange = new EventEmitter();
        this.allSelected = false;
        this.overflowCommand$ = new Subject();
        this.scrollCommand$ = new Subject();
        this.dataCommand$ = new BehaviorSubject([]);
        this.stateCommand$ = new Subject();
        this.stateInputCommand$ = new BehaviorSubject({
            sort: { field: '', type: 'none' },
            filter: [],
        });
        this.columnsChangeCommand$ = new Subject();
        this._subscriptions = new Subscriber();
        this._keys = [];
    }
    set data(elements) {
        this.dataCommand$.next(elements);
    }
    set state(event) {
        this.stateInputCommand$.next(event);
    }
    get columns() {
        var _a, _b;
        return (_b = (_a = this.expandable) === null || _a === void 0 ? void 0 : _a.columns) !== null && _b !== void 0 ? _b : this._columns;
    }
    get footer() {
        return this.columns.some((column) => !!column.footer);
    }
    ngOnInit() {
        var _a;
        this.state$ = combineLatest([
            this.stateInputCommand$,
            this.columnsChangeCommand$.pipe(startWith(null)),
        ])
            .pipe(map(([event]) => event), map((event) => TableState.from(event, this.columns.toArray())))
            .pipe(switchMap((state) => this.stateCommand$.pipe(scan((state, event) => state.update(event), state), tap((state) => this.stateChange.emit(state.event())), startWith(state))), shareReplay(1));
        const selecteds$ = iif(() => !!this.checkbox, (_a = this.checkbox) === null || _a === void 0 ? void 0 : _a.selected$, of(TableSelectionState.none()));
        this.elements$ = combineLatest([
            this.dataCommand$,
            selecteds$,
            this.state$,
        ]).pipe(tap(([data, selected]) => {
            this.allSelected = data.length === selected.current().length;
        }), map(([data, selecteds, state]) => new TableDataTransform(data, selecteds, state, this.trackBy)), map((state) => state.elements()), tap((elements) => {
            this._keys = elements.map((element) => element.key);
        }));
        this._scrollEvents();
    }
    ngAfterViewInit() {
        // We need to subscribe here because on OnInit columns is undefined
        // Query objects are seted before ngAfterViewInit call
        this._subscriptions.add(this.columns.changes.subscribe(() => {
            this.columnsChangeCommand$.next(true);
        }));
    }
    ngOnDestroy() {
        this._subscriptions.unsubscribe();
    }
    ngAfterViewChecked() {
        this.overflowCommand$.next();
    }
    onElementSelected(element) {
        var _a;
        (_a = this.checkbox) === null || _a === void 0 ? void 0 : _a.select(element.key);
    }
    onSelectAll(event) {
        var _a;
        const checked = event.target.checked;
        (_a = this.checkbox) === null || _a === void 0 ? void 0 : _a.select(checked && !this.allSelected ? this._keys : []);
    }
    onScroll() {
        this.scrolled.emit();
    }
    onScrollLeft() {
        this.scrollCommand$.next('left');
    }
    onScrollRight() {
        this.scrollCommand$.next('right');
    }
    onStateChange(event) {
        this.stateCommand$.next(event);
    }
    trackByProperty(_, element) {
        return element.trackBy;
    }
    _scrollEvents() {
        const overflow$ = this.overflowCommand$.pipe(filter(() => !!this.container), map(() => this._keys.length), distinctUntilChanged(), map(() => new HorizontalScroll(this.container)), filter((scroll) => scroll.overflowed()), shareReplay());
        const nativeScroll$ = fromEvent(this.container.nativeElement, 'scroll').pipe(map(() => new HorizontalScroll(this.container)), shareReplay());
        const left$ = this.scrollCommand$.pipe(filter((type) => type === 'left'), map(() => new HorizontalScroll(this.container)), tap((scroll) => scroll.scrollLeft()));
        const right$ = this.scrollCommand$.pipe(filter((type) => type === 'right'), map(() => new HorizontalScroll(this.container)), tap((scroll) => scroll.scrollRight()));
        const scroll$ = merge(left$, right$).pipe(shareReplay());
        this.scrollLeft$ = merge(overflow$, scroll$, nativeScroll$).pipe(filter(() => !this.disableHorizontalScrollingButtons), map((scroll) => scroll.canScrollLeft()), debounceTime(50));
        this.scrollRight$ = merge(overflow$, scroll$, nativeScroll$).pipe(filter(() => !this.disableHorizontalScrollingButtons), map((scroll) => scroll.canScrollRight()), debounceTime(50));
        this.overflowed$ = overflow$.pipe(map((scroll) => scroll.overflowed()));
    }
}
TableComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
TableComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.12", type: TableComponent, selector: "ps-ui-table", inputs: { data: "data", scroll: "scroll", border: "border", trackBy: "trackBy", state: "state", headless: "headless", disableHorizontalScrollingButtons: "disableHorizontalScrollingButtons" }, outputs: { scrolled: "scrolled", stateChange: "stateChange" }, queries: [{ propertyName: "checkbox", first: true, predicate: TableCheckboxColumnDirective, descendants: true, static: true }, { propertyName: "expandable", first: true, predicate: TableExpandableRowDirective, descendants: true }, { propertyName: "caption", first: true, predicate: TableCaptionDirective, descendants: true }, { propertyName: "_columns", predicate: TableColumnDirective }], viewQueries: [{ propertyName: "container", first: true, predicate: ["tableContainer"], descendants: true, static: true }], ngImport: i0, template: "<div\n  class=\"table-responsive table-overflow ps-table\"\n  [ngClass]=\"{ 'ps-table-overflow': overflowed$ | async }\"\n  infiniteScroll\n  [infiniteScrollDistance]=\"scroll.distance\"\n  [infiniteScrollThrottle]=\"scroll.throttle\"\n  (scrolled)=\"onScroll()\"\n>\n  <button\n    *ngIf=\"scrollLeft$ | async\"\n    class=\"ps-table-scroll-left scroll-button\"\n    psUiLongPress\n    type=\"button\"\n    (click)=\"onScrollLeft()\"\n    (longPress)=\"onScrollLeft()\"\n  >\n    <i class=\"fa fa-caret-left fa-3x scroll-button\" aria-hidden=\"true\"> </i>\n  </button>\n  <div class=\"table-container\" #tableContainer>\n    <table class=\"table\">\n      <thead *ngIf=\"!headless\">\n        <ng-container *ngIf=\"state$ | async as state\">\n          <tr>\n            <th\n              class=\"checkbox table-stick-column\"\n              *ngIf=\"checkbox && checkbox.show$ | async\"\n            >\n              <label *ngIf=\"checkbox!.showSelectAll\" class=\"checkbox-all\">\n                <input\n                  type=\"checkbox\"\n                  [checked]=\"allSelected\"\n                  (change)=\"onSelectAll($event)\"\n                  [disabled]=\"checkbox!.disable\"\n                />\n              </label>\n            </th>\n            <ng-container *ngFor=\"let column of columns\">\n              <th\n                *ngIf=\"column.show$ | async\"\n                [ngStyle]=\"{ width: column.space() }\"\n              >\n                <ng-container\n                  *ngIf=\"column.header\"\n                  [ngTemplateOutlet]=\"column.header.template\"\n                ></ng-container>\n                <ps-ui-table-column-header\n                  *ngIf=\"!column.header\"\n                  [title]=\"column.title\"\n                  [items]=\"(column.filters$ | async) || []\"\n                  [groups]=\"(column.groups$ | async) || {}\"\n                  [field]=\"column.field\"\n                  [multi]=\"column.multi\"\n                  [searchDropdownItems]=\"column.searchDropdownItems\"\n                  [showClearStateButton]=\"column.showClearStateButton\"\n                  [filterable]=\"(column.filterable$ | async) || false\"\n                  [searchable]=\"column.searchable\"\n                  [sortable]=\"column.sortable\"\n                  [textAlignment]=\"column.textAlignment\"\n                  [sorted]=\"state.sorted(column.field)\"\n                  [filtered]=\"state.filtered(column.field)\"\n                  [truncateWord]=\"column.truncateWord\"\n                  [delegate]=\"column.delegate\"\n                  [searchType]=\"column.searchType\"\n                  (sort)=\"onStateChange($event)\"\n                  (filter)=\"onStateChange($event)\"\n                ></ps-ui-table-column-header>\n              </th>\n            </ng-container>\n          </tr>\n        </ng-container>\n      </thead>\n      <tbody>\n        <ng-container\n          *ngFor=\"\n            let element of elements$ | async;\n            index as i;\n            trackBy: trackByProperty\n          \"\n        >\n          <tr [ngClass]=\"{ selected: element.selected }\">\n            <ng-container>\n              <td\n                class=\"checkbox table-stick-column table-border-{{ border }}\"\n                *ngIf=\"checkbox && checkbox.show$ | async\"\n              >\n                <label>\n                  <input\n                    type=\"checkbox\"\n                    [checked]=\"element.selected\"\n                    (change)=\"onElementSelected(element)\"\n                    [disabled]=\"checkbox!.disable\"\n                  />\n                </label>\n              </td>\n            </ng-container>\n\n            <ng-container *ngFor=\"let column of columns\">\n              <td\n                *ngIf=\"column.show$ | async\"\n                class=\"table-border-{{ border }}\"\n                [ngStyle]=\"{ width: column.space() }\"\n                (click)=\"column.content.tap.emit(element.data)\"\n              >\n                <ng-container\n                  [ngTemplateOutlet]=\"column.template\"\n                  [ngTemplateOutletContext]=\"{\n                    $implicit: element.data,\n                    index: i\n                  }\"\n                >\n                </ng-container>\n              </td>\n            </ng-container>\n          </tr>\n          <ng-container *ngIf=\"expandable?.expanded$ | async as row\">\n            <tr class=\"no-highlight\" *ngIf=\"row.expanded(i)\">\n              <td\n                class=\"expandable-content\"\n                [attr.colspan]=\"columns.length + 1\"\n              >\n                <ng-container\n                  [ngTemplateOutlet]=\"expandable!.template\"\n                  [ngTemplateOutletContext]=\"{\n                    $implicit: element.data,\n                    index: i\n                  }\"\n                >\n                </ng-container>\n              </td>\n            </tr>\n          </ng-container>\n        </ng-container>\n\n        <ng-container *ngIf=\"footer\">\n          <tr>\n            <td\n              class=\"checkbox table-stick-column table-border-{{ border }}\"\n              *ngIf=\"checkbox && checkbox.show$ | async\"\n            ></td>\n            <ng-container *ngFor=\"let column of columns; index as j\">\n              <td\n                *ngIf=\"column.show$ | async\"\n                class=\"table-border-{{ border }}\"\n                [ngStyle]=\"{ width: column.space() }\"\n              >\n                <ng-container\n                  *ngIf=\"column.footer\"\n                  [ngTemplateOutlet]=\"column.footer.template\"\n                  [ngTemplateOutletContext]=\"{\n                    $implicit: column,\n                    index: j\n                  }\"\n                >\n                </ng-container>\n              </td>\n            </ng-container>\n          </tr>\n        </ng-container>\n      </tbody>\n      <ng-container *ngIf=\"caption\">\n        <caption [ngStyle]=\"{ 'caption-side': caption.placement }\">\n          <ng-container [ngTemplateOutlet]=\"caption.template\"></ng-container>\n        </caption>\n      </ng-container>\n    </table>\n  </div>\n  <button\n    *ngIf=\"scrollRight$ | async\"\n    type=\"button\"\n    psUiLongPress\n    (click)=\"onScrollRight()\"\n    (longPress)=\"onScrollRight()\"\n    class=\"ps-table-scroll-right scroll-button\"\n  >\n    <i class=\"fa fa-caret-right fa-3x scroll-button\" aria-hidden=\"true\"> </i>\n  </button>\n</div>\n", styles: [":host{--custom-dropdown-background-color: var( --custom-table-header-background, #333 );--custom-dropdown-border-color: var(--custom-table-header-background, #333);--custom-dropdown-font-family: var(--custom-table-header-font-family);--custom-dropdown-padding: var(--custom-table-header-padding, 0);--custom-dropdown-color: var(--custom-table-header-color, #8d959d);--custom-dropdown-font-size: var(--custom-table-header-font-size, 11px);--custom-dropdown-border-radius: var( --custom-table-header-border-radius, 3px );--custom-dropdown-button-icon-position: unset;--custom-dropdown-action-button-width: auto;--custom-dropdown-padding: 6px 12px;--custom-dropdown-border-radius: 0;.cdk-virtual-scroll-orientation-vertical .cdk-virtual-scroll-content-wrapper {position: unset; border: 1px solid rgba(0,0,0,.15); border-bottom: 2px solid rgba(0,0,0,.15);} .dropdown-button {box-shadow: none !important;}}.ps-table{min-height:var(--custom-table-min-height, 300px);max-height:var(--custom-table-max-height);position:relative}table{overflow:var(--custom-table-element-overflow, clip)}tbody tr{background-color:var(--custom-table-background-color, #fff)}td{background-color:inherit}.ps-table-overflow{padding:var(--custom-table-overflow-padding, 0 20px)}.ps-table div{overflow:auto;overflow-y:hidden;min-height:var(--custom-table-min-height, 300px)}.ps-table-scroll-left{position:absolute;left:0;top:0}.ps-table-scroll-right{position:absolute;right:0;top:0}.scroll-button{border:none;background-color:var( --custom-table-scroll-button-background-color, transparent );padding:0;margin:0;height:var(--custom-tabble-scroll-button-height, 40px);width:var(--custom-tabble-scroll-button-width, 20px)}table thead th{background-color:var(--custom-table-header-background, #333);color:var(--custom-table-header-color, #8d959d);font-size:var(--custom-table-header-font-size, 11px);font-weight:var(--custom-table-header-font-weight, 400);letter-spacing:var(--custom-table-header-font-spacing, .4px);text-align:var(--custom-table-header-text-aling, left);padding:var(--custom-table-header-padding, 8px 0px 8px 8px);word-break:var(--custom-table-header-word-break, keep-all)}table thead p{margin:var(--custom-table-header-title-margin, 0)}table tr:hover{background:var(--custom-table-row-hover-color, #f2f2f2)!important;cursor:pointer}table tr.no-highlight:hover{background:unset}table tr.selected td{background:var(--custom-table-row-selected-color, #f2f2f2)}table tr.selected td{background:var(--custom-table-row-selected-color, #f2f2f2);color:var(--custom-table-row-selected-cell-color, #293541)}table thead tr{background-color:transparent}table tr td{padding:var(--custom-table-cell-padding, 20px 14px);color:var(--custom-table-cell-color, #293541);text-align:var(--custom-table-cell-text-aling, left)}th.checkbox{width:1%;white-space:nowrap;padding:8px 14px 5px!important}th.checkbox-all{width:1%;white-space:nowrap;padding:8px 14px 5px!important;background-color:#333}.table-border-bottom{border-bottom:var(--custom-table-border, 1px solid #c8ced4)!important}.table-border-all{border:var(--custom-table-border, 1px solid #c8ced4)!important}.table-border-none{border:none!important}.table-stick-column{position:sticky;left:0;z-index:1;background-color:inherit}td.table-stick-column:hover{background:var(--custom-table-row-hover-color, #f2f2f2)!important}.scroll-button{color:var(--custom-table-scroll-button-color, #8d959d)}.table-container{padding:0}@-moz-document url-prefix(){table{overflow-y:unset!important}}thead{z-index:var(--custom-ps-ui-table-header-z-index)}\n"], components: [{ type: TableColumnHeaderComponent, selector: "ps-ui-table-column-header", inputs: ["title", "items", "field", "searchable", "multi", "searchDropdownItems", "showClearStateButton", "sortable", "filterable", "sorted", "groups", "filtered", "truncateWord", "textAlignment", "delegate", "searchType"], outputs: ["sort", "filter"] }], directives: [{ type: i2.InfiniteScrollDirective, selector: "[infiniteScroll], [infinite-scroll], [data-infinite-scroll]", inputs: ["infiniteScrollDistance", "infiniteScrollUpDistance", "infiniteScrollThrottle", "infiniteScrollDisabled", "infiniteScrollContainer", "scrollWindow", "immediateCheck", "horizontal", "alwaysCallback", "fromRoot"], outputs: ["scrolled", "scrolledUp"] }, { type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: LongPressDirective, selector: "[psUiLongPress]", inputs: ["threshold"], outputs: ["longPress"] }, { type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i3.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { type: i3.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }], pipes: { "async": i3.AsyncPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ps-ui-table', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div\n  class=\"table-responsive table-overflow ps-table\"\n  [ngClass]=\"{ 'ps-table-overflow': overflowed$ | async }\"\n  infiniteScroll\n  [infiniteScrollDistance]=\"scroll.distance\"\n  [infiniteScrollThrottle]=\"scroll.throttle\"\n  (scrolled)=\"onScroll()\"\n>\n  <button\n    *ngIf=\"scrollLeft$ | async\"\n    class=\"ps-table-scroll-left scroll-button\"\n    psUiLongPress\n    type=\"button\"\n    (click)=\"onScrollLeft()\"\n    (longPress)=\"onScrollLeft()\"\n  >\n    <i class=\"fa fa-caret-left fa-3x scroll-button\" aria-hidden=\"true\"> </i>\n  </button>\n  <div class=\"table-container\" #tableContainer>\n    <table class=\"table\">\n      <thead *ngIf=\"!headless\">\n        <ng-container *ngIf=\"state$ | async as state\">\n          <tr>\n            <th\n              class=\"checkbox table-stick-column\"\n              *ngIf=\"checkbox && checkbox.show$ | async\"\n            >\n              <label *ngIf=\"checkbox!.showSelectAll\" class=\"checkbox-all\">\n                <input\n                  type=\"checkbox\"\n                  [checked]=\"allSelected\"\n                  (change)=\"onSelectAll($event)\"\n                  [disabled]=\"checkbox!.disable\"\n                />\n              </label>\n            </th>\n            <ng-container *ngFor=\"let column of columns\">\n              <th\n                *ngIf=\"column.show$ | async\"\n                [ngStyle]=\"{ width: column.space() }\"\n              >\n                <ng-container\n                  *ngIf=\"column.header\"\n                  [ngTemplateOutlet]=\"column.header.template\"\n                ></ng-container>\n                <ps-ui-table-column-header\n                  *ngIf=\"!column.header\"\n                  [title]=\"column.title\"\n                  [items]=\"(column.filters$ | async) || []\"\n                  [groups]=\"(column.groups$ | async) || {}\"\n                  [field]=\"column.field\"\n                  [multi]=\"column.multi\"\n                  [searchDropdownItems]=\"column.searchDropdownItems\"\n                  [showClearStateButton]=\"column.showClearStateButton\"\n                  [filterable]=\"(column.filterable$ | async) || false\"\n                  [searchable]=\"column.searchable\"\n                  [sortable]=\"column.sortable\"\n                  [textAlignment]=\"column.textAlignment\"\n                  [sorted]=\"state.sorted(column.field)\"\n                  [filtered]=\"state.filtered(column.field)\"\n                  [truncateWord]=\"column.truncateWord\"\n                  [delegate]=\"column.delegate\"\n                  [searchType]=\"column.searchType\"\n                  (sort)=\"onStateChange($event)\"\n                  (filter)=\"onStateChange($event)\"\n                ></ps-ui-table-column-header>\n              </th>\n            </ng-container>\n          </tr>\n        </ng-container>\n      </thead>\n      <tbody>\n        <ng-container\n          *ngFor=\"\n            let element of elements$ | async;\n            index as i;\n            trackBy: trackByProperty\n          \"\n        >\n          <tr [ngClass]=\"{ selected: element.selected }\">\n            <ng-container>\n              <td\n                class=\"checkbox table-stick-column table-border-{{ border }}\"\n                *ngIf=\"checkbox && checkbox.show$ | async\"\n              >\n                <label>\n                  <input\n                    type=\"checkbox\"\n                    [checked]=\"element.selected\"\n                    (change)=\"onElementSelected(element)\"\n                    [disabled]=\"checkbox!.disable\"\n                  />\n                </label>\n              </td>\n            </ng-container>\n\n            <ng-container *ngFor=\"let column of columns\">\n              <td\n                *ngIf=\"column.show$ | async\"\n                class=\"table-border-{{ border }}\"\n                [ngStyle]=\"{ width: column.space() }\"\n                (click)=\"column.content.tap.emit(element.data)\"\n              >\n                <ng-container\n                  [ngTemplateOutlet]=\"column.template\"\n                  [ngTemplateOutletContext]=\"{\n                    $implicit: element.data,\n                    index: i\n                  }\"\n                >\n                </ng-container>\n              </td>\n            </ng-container>\n          </tr>\n          <ng-container *ngIf=\"expandable?.expanded$ | async as row\">\n            <tr class=\"no-highlight\" *ngIf=\"row.expanded(i)\">\n              <td\n                class=\"expandable-content\"\n                [attr.colspan]=\"columns.length + 1\"\n              >\n                <ng-container\n                  [ngTemplateOutlet]=\"expandable!.template\"\n                  [ngTemplateOutletContext]=\"{\n                    $implicit: element.data,\n                    index: i\n                  }\"\n                >\n                </ng-container>\n              </td>\n            </tr>\n          </ng-container>\n        </ng-container>\n\n        <ng-container *ngIf=\"footer\">\n          <tr>\n            <td\n              class=\"checkbox table-stick-column table-border-{{ border }}\"\n              *ngIf=\"checkbox && checkbox.show$ | async\"\n            ></td>\n            <ng-container *ngFor=\"let column of columns; index as j\">\n              <td\n                *ngIf=\"column.show$ | async\"\n                class=\"table-border-{{ border }}\"\n                [ngStyle]=\"{ width: column.space() }\"\n              >\n                <ng-container\n                  *ngIf=\"column.footer\"\n                  [ngTemplateOutlet]=\"column.footer.template\"\n                  [ngTemplateOutletContext]=\"{\n                    $implicit: column,\n                    index: j\n                  }\"\n                >\n                </ng-container>\n              </td>\n            </ng-container>\n          </tr>\n        </ng-container>\n      </tbody>\n      <ng-container *ngIf=\"caption\">\n        <caption [ngStyle]=\"{ 'caption-side': caption.placement }\">\n          <ng-container [ngTemplateOutlet]=\"caption.template\"></ng-container>\n        </caption>\n      </ng-container>\n    </table>\n  </div>\n  <button\n    *ngIf=\"scrollRight$ | async\"\n    type=\"button\"\n    psUiLongPress\n    (click)=\"onScrollRight()\"\n    (longPress)=\"onScrollRight()\"\n    class=\"ps-table-scroll-right scroll-button\"\n  >\n    <i class=\"fa fa-caret-right fa-3x scroll-button\" aria-hidden=\"true\"> </i>\n  </button>\n</div>\n", styles: [":host{--custom-dropdown-background-color: var( --custom-table-header-background, #333 );--custom-dropdown-border-color: var(--custom-table-header-background, #333);--custom-dropdown-font-family: var(--custom-table-header-font-family);--custom-dropdown-padding: var(--custom-table-header-padding, 0);--custom-dropdown-color: var(--custom-table-header-color, #8d959d);--custom-dropdown-font-size: var(--custom-table-header-font-size, 11px);--custom-dropdown-border-radius: var( --custom-table-header-border-radius, 3px );--custom-dropdown-button-icon-position: unset;--custom-dropdown-action-button-width: auto;--custom-dropdown-padding: 6px 12px;--custom-dropdown-border-radius: 0;.cdk-virtual-scroll-orientation-vertical .cdk-virtual-scroll-content-wrapper {position: unset; border: 1px solid rgba(0,0,0,.15); border-bottom: 2px solid rgba(0,0,0,.15);} .dropdown-button {box-shadow: none !important;}}.ps-table{min-height:var(--custom-table-min-height, 300px);max-height:var(--custom-table-max-height);position:relative}table{overflow:var(--custom-table-element-overflow, clip)}tbody tr{background-color:var(--custom-table-background-color, #fff)}td{background-color:inherit}.ps-table-overflow{padding:var(--custom-table-overflow-padding, 0 20px)}.ps-table div{overflow:auto;overflow-y:hidden;min-height:var(--custom-table-min-height, 300px)}.ps-table-scroll-left{position:absolute;left:0;top:0}.ps-table-scroll-right{position:absolute;right:0;top:0}.scroll-button{border:none;background-color:var( --custom-table-scroll-button-background-color, transparent );padding:0;margin:0;height:var(--custom-tabble-scroll-button-height, 40px);width:var(--custom-tabble-scroll-button-width, 20px)}table thead th{background-color:var(--custom-table-header-background, #333);color:var(--custom-table-header-color, #8d959d);font-size:var(--custom-table-header-font-size, 11px);font-weight:var(--custom-table-header-font-weight, 400);letter-spacing:var(--custom-table-header-font-spacing, .4px);text-align:var(--custom-table-header-text-aling, left);padding:var(--custom-table-header-padding, 8px 0px 8px 8px);word-break:var(--custom-table-header-word-break, keep-all)}table thead p{margin:var(--custom-table-header-title-margin, 0)}table tr:hover{background:var(--custom-table-row-hover-color, #f2f2f2)!important;cursor:pointer}table tr.no-highlight:hover{background:unset}table tr.selected td{background:var(--custom-table-row-selected-color, #f2f2f2)}table tr.selected td{background:var(--custom-table-row-selected-color, #f2f2f2);color:var(--custom-table-row-selected-cell-color, #293541)}table thead tr{background-color:transparent}table tr td{padding:var(--custom-table-cell-padding, 20px 14px);color:var(--custom-table-cell-color, #293541);text-align:var(--custom-table-cell-text-aling, left)}th.checkbox{width:1%;white-space:nowrap;padding:8px 14px 5px!important}th.checkbox-all{width:1%;white-space:nowrap;padding:8px 14px 5px!important;background-color:#333}.table-border-bottom{border-bottom:var(--custom-table-border, 1px solid #c8ced4)!important}.table-border-all{border:var(--custom-table-border, 1px solid #c8ced4)!important}.table-border-none{border:none!important}.table-stick-column{position:sticky;left:0;z-index:1;background-color:inherit}td.table-stick-column:hover{background:var(--custom-table-row-hover-color, #f2f2f2)!important}.scroll-button{color:var(--custom-table-scroll-button-color, #8d959d)}.table-container{padding:0}@-moz-document url-prefix(){table{overflow-y:unset!important}}thead{z-index:var(--custom-ps-ui-table-header-z-index)}\n"] }]
        }], propDecorators: { data: [{
                type: Input
            }], scroll: [{
                type: Input
            }], border: [{
                type: Input
            }], trackBy: [{
                type: Input
            }], state: [{
                type: Input
            }], headless: [{
                type: Input
            }], disableHorizontalScrollingButtons: [{
                type: Input
            }], scrolled: [{
                type: Output
            }], stateChange: [{
                type: Output
            }], _columns: [{
                type: ContentChildren,
                args: [TableColumnDirective]
            }], checkbox: [{
                type: ContentChild,
                args: [TableCheckboxColumnDirective, { static: true }]
            }], expandable: [{
                type: ContentChild,
                args: [TableExpandableRowDirective]
            }], caption: [{
                type: ContentChild,
                args: [TableCaptionDirective]
            }], container: [{
                type: ViewChild,
                args: ['tableContainer', { static: true }]
            }] } });

class TableModule {
}
TableModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TableModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableModule, declarations: [TableComponent,
        TableColumnDirective,
        TableColumnHeaderComponent,
        TableColumnHeaderOptionDirective,
        TableColumnCustomHeaderDirective,
        TableCheckboxColumnDirective,
        TableCellTemplateDirective,
        TableExpandableRowDirective,
        TableExpandableContentDirective,
        TableColumnFilterDirective,
        TableColumnFooterDirective,
        TableCaptionDirective,
        LongPressDirective], imports: [CommonModule,
        InfiniteScrollModule,
        SelectDropdownModule,
        ButtonModule,
        ReactiveFormsModule], exports: [TableComponent,
        TableColumnDirective,
        TableColumnHeaderComponent,
        TableColumnHeaderOptionDirective,
        TableColumnCustomHeaderDirective,
        TableCheckboxColumnDirective,
        TableCellTemplateDirective,
        TableExpandableRowDirective,
        TableExpandableContentDirective,
        TableColumnFilterDirective,
        TableColumnFooterDirective,
        TableCaptionDirective,
        LongPressDirective] });
TableModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableModule, providers: [], imports: [[
            CommonModule,
            InfiniteScrollModule,
            SelectDropdownModule,
            ButtonModule,
            ReactiveFormsModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TableModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        InfiniteScrollModule,
                        SelectDropdownModule,
                        ButtonModule,
                        ReactiveFormsModule,
                    ],
                    declarations: [
                        TableComponent,
                        TableColumnDirective,
                        TableColumnHeaderComponent,
                        TableColumnHeaderOptionDirective,
                        TableColumnCustomHeaderDirective,
                        TableCheckboxColumnDirective,
                        TableCellTemplateDirective,
                        TableExpandableRowDirective,
                        TableExpandableContentDirective,
                        TableColumnFilterDirective,
                        TableColumnFooterDirective,
                        TableCaptionDirective,
                        LongPressDirective,
                    ],
                    exports: [
                        TableComponent,
                        TableColumnDirective,
                        TableColumnHeaderComponent,
                        TableColumnHeaderOptionDirective,
                        TableColumnCustomHeaderDirective,
                        TableCheckboxColumnDirective,
                        TableCellTemplateDirective,
                        TableExpandableRowDirective,
                        TableExpandableContentDirective,
                        TableColumnFilterDirective,
                        TableColumnFooterDirective,
                        TableCaptionDirective,
                        LongPressDirective,
                    ],
                    providers: [],
                }]
        }] });

const EMPTY_TABLE_EVENT = {
    column: '',
    field: '',
    sort: 'none',
    filter: [],
};

/**
 * Generated bundle index. Do not edit.
 */

export { CustomFilter, EMPTY_TABLE_EVENT, EmptyFilter, FilterValue, LongPressDirective, PartialFilter, StrictFilter, TableCaptionDirective, TableCellTemplateDirective, TableCheckboxColumnDirective, TableColumnCustomHeaderDirective, TableColumnDirective, TableColumnFilterDirective, TableColumnFooterDirective, TableColumnHeaderComponent, TableColumnHeaderOptionDirective, TableComponent, TableExpandableContentDirective, TableExpandableRowDirective, TableFilter, TableFilterFactory, TableModule };
